import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getGlobalSettings,
  updateTelegramSettings,
  updatePositionDiscussion,
  updatePushoverSettings,
  updateOrderbookDefaults,
  updateVertexAISettings,
  updateArkhamSettings,
  updateStorageBoxSettings,
  getStateStatus,
  clearState,
  updateOIAlertsIgnore,
  updateOrderbookAlertsIgnore,
  updateMarkPriceAlertsIgnore,
  getNotificationUsers,
  getAuthUsers,
  createAuthUser,
  updateAuthUser,
  deleteAuthUser,
  getTradePatternSettings,
  updateTradePatternSettings,
} from '@/lib/api'
import type { TradePatternSettings } from '@/types'

export function useGlobalSettings() {
  return useQuery({
    queryKey: ['settings', 'global'],
    queryFn: getGlobalSettings,
  })
}

export function useTradePatternSettings(enabled: boolean = true) {
  return useQuery({
    queryKey: ['settings', 'trade-patterns'],
    queryFn: getTradePatternSettings,
    enabled,
  })
}

export function useUpdateTradePatternSettings() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ profile, config }: { profile: TradePatternSettings['profile']; config: TradePatternSettings }) =>
      updateTradePatternSettings(profile, config),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['settings', 'trade-patterns'] })
    },
  })
}

export function useNotificationUsers() {
  return useQuery({
    queryKey: ['settings', 'users'],
    queryFn: getNotificationUsers,
  })
}

export function useUpdateTelegramSettings() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: updateTelegramSettings,
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ['settings'] })
    },
  })
}

export function useUpdatePositionDiscussion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: updatePositionDiscussion,
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ['settings'] })
    },
  })
}

export function useUpdatePushoverSettings() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: updatePushoverSettings,
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ['settings'] })
    },
  })
}

export function useUpdateOrderbookDefaults() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: updateOrderbookDefaults,
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ['settings'] })
    },
  })
}

export function useUpdateVertexAISettings() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: updateVertexAISettings,
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ['settings'] })
    },
  })
}

export function useUpdateArkhamSettings() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: updateArkhamSettings,
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ['settings'] })
    },
  })
}

export function useUpdateStorageBoxSettings() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: updateStorageBoxSettings,
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ['settings'] })
    },
  })
}

export function useStateStatus(enabled: boolean = true) {
  return useQuery({
    queryKey: ['state', 'status'],
    queryFn: getStateStatus,
    enabled,
    retry: false,
    staleTime: 0, // Always fetch fresh data
  })
}

export function useClearState() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: clearState,
    onSuccess: async (data) => {
      if (data?.categories && data?.components) {
        queryClient.setQueryData(['state', 'status'], {
          categories: data.categories,
          components: data.components,
        })
        return
      }
      await queryClient.invalidateQueries({ queryKey: ['state', 'status'] })
    },
  })
}

export function useUpdateOIAlertsIgnore() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: updateOIAlertsIgnore,
    onSuccess: async () => {
      // Invalidate all OI alert queries including status (which contains ignore list)
      await queryClient.invalidateQueries({ queryKey: ['alerts', 'oi'] })
      // Also invalidate global settings
      await queryClient.invalidateQueries({ queryKey: ['settings', 'global'] })
    },
  })
}

export function useUpdateOrderbookAlertsIgnore() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: updateOrderbookAlertsIgnore,
    onSuccess: async () => {
      // Invalidate all orderbook queries including status
      await queryClient.invalidateQueries({ queryKey: ['alerts', 'orderbook'] })
      // Also invalidate global settings which contains orderbook_alerts_ignore
      await queryClient.invalidateQueries({ queryKey: ['settings', 'global'] })
    },
  })
}

export function useUpdateMarkPriceAlertsIgnore() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: updateMarkPriceAlertsIgnore,
    onSuccess: async () => {
      // Invalidate all mark price queries including status (which contains ignore list)
      await queryClient.invalidateQueries({ queryKey: ['alerts', 'markprice'] })
      // Also invalidate global settings
      await queryClient.invalidateQueries({ queryKey: ['settings', 'global'] })
    },
  })
}

// User Management hooks (Admin only)
export function useAuthUsers() {
  return useQuery({
    queryKey: ['auth', 'users'],
    queryFn: getAuthUsers,
  })
}

export function useCreateAuthUser() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: createAuthUser,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['auth', 'users'] })
    },
  })
}

export function useUpdateAuthUser() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ username, updates }: { username: string; updates: Parameters<typeof updateAuthUser>[1] }) =>
      updateAuthUser(username, updates),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['auth', 'users'] })
    },
  })
}

export function useDeleteAuthUser() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: deleteAuthUser,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['auth', 'users'] })
    },
  })
}
